import { ProgramOption, ProgramResponse } from "./types";
import { GetFaqPayload, UpdateFaqPayload } from "./types";

// Use REACT_APP_ prefix for environment variables accessible in the browser
const FAQ_API_ENDPOINT = process.env.REACT_APP_BOT_END_POINT;
const GRAPHQL_ENDPOINT = process.env.REACT_APP_PRISM_END_POINT;

interface CreateFaqPayload {
  faqs: { question: string; answer: string }[];
  globalVisibility: boolean;
  selectedPrograms: ProgramOption[];
}

// create a faq
export const createFaq = async (payload: CreateFaqPayload): Promise<any> => {
  const { faqs, globalVisibility, selectedPrograms } = payload;
  const maiAskUser = JSON.parse(localStorage.getItem("maiAskUser") || "{}");
  const requestBody: any = {
    input: {
      faqs: faqs.map((faq) => ({
        question: faq.question.trim(),
        answer: faq.answer.trim(),
      })),
      is_global: globalVisibility,
      email: maiAskUser.email,
      prism_id: maiAskUser.prismId,
    },
  };

  if (!globalVisibility && selectedPrograms && selectedPrograms.length > 0) {
    requestBody.input.programs = selectedPrograms
      .filter((program) => program.code !== undefined)
      .map((program) => ({
        name: program.label,
        code: program.code!,
        pok: program.pok || null,
      }));
  }

  const response = await fetch(`${FAQ_API_ENDPOINT}/add_faqs/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.text();
    console.error("Failed to create FAQs:", response.status, errorData);
    throw new Error(`Failed to create FAQs. Status: ${response.status}`);
  }

  const data = await response.json();
  if (!data.output?.status) {
    throw new Error(data.output?.message || "Failed to create FAQs");
  }

  return data;
};

// fetch programs
export const fetchPrograms = async (
  searchQuery: string,
  code?: string[],
  skipValue: number = 0
): Promise<ProgramOption[]> => {
  try {
    if (code && code.length === 0 && !searchQuery) {
      return [];
    }

    const graphqlQuery = `query latestPrograms($where: ProgramWhereInput, $limit: Int, $skip: Int, $sort: LatestProgramSortInput) {
          latestPrograms(where: $where, limit: $limit, skip: $skip, sort: $sort) {
            result {
              id
              name
              code
              pok
            }
          }
        }`;

    const variables: any = {
      where: {},
      skip: skipValue,
      limit: 50,
    };

    if (code && code.length > 0) {
      variables.where.code = {
        _in: code,
      };
    } else {
      variables.where.name = {
        _regex: searchQuery || "",
        _options: "$i",
      };
      variables.where.workshop = {
        _exists: false,
      };
    }

    // Get auth token from environment variable
    const AUTH_TOKEN = "Bearer " + localStorage.getItem("maiaskToken");

    const response = await fetch(GRAPHQL_ENDPOINT!, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: AUTH_TOKEN,
        role: "manager",
      },
      body: JSON.stringify({
        query: graphqlQuery,
        variables: variables,
      }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error("Failed to fetch programs:", response.status, errorData);
      throw new Error(`Failed to fetch programs. Status: ${response.status}`);
    }

    const data: ProgramResponse = await response.json();

    // Check for GraphQL errors
    if (data.errors) {
      console.error("GraphQL Errors:", data.errors);
      throw new Error("Failed to fetch programs due to GraphQL errors.");
    }

    // Safely access nested data
    const programsResult = data?.data?.latestPrograms?.result;
    if (!Array.isArray(programsResult)) {
      console.error("Invalid program data structure received:", data);
      // Return empty array or throw error based on how critical this is
      return []; // Returning empty array for now
      // throw new Error("Received invalid data structure for programs.");
    }

    const programs = programsResult.map((program) => ({
      label: program.name,
      value: program.code,
      code: program.code,
      pok: program.pok,
    }));

    return programs;
  } catch (error) {
    console.error("Error fetching programs:", error);
    // Re-throw the error to allow the caller component to handle it
    throw error;
  }
};

// fetch faqs based on parameters
export const getFaqs = async (payload: GetFaqPayload): Promise<any> => {
  // Validate that at least one key is present
  const hasKey = Object.keys(payload).some(
    (key) =>
      payload[key as keyof GetFaqPayload] !== undefined &&
      payload[key as keyof GetFaqPayload] !== null
  );

  if (!hasKey) {
    throw new Error(
      "At least one parameter (program_code, question, pok, or faq_id) is required to fetch FAQs."
    );
  }

  if (!FAQ_API_ENDPOINT) {
    throw new Error(
      "FAQ API endpoint is not configured in environment variables."
    );
  }

  const response = await fetch(`${FAQ_API_ENDPOINT}/get_faqs/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ input: payload }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    console.error("Failed to fetch FAQs:", response.status, errorData);
    throw new Error(`Failed to fetch FAQs. Status: ${response.status}`);
  }
  return response.json();
};

// delete faq by id
export const deleteFaq = async (faqId: string): Promise<any> => {
  if (!faqId) {
    throw new Error("FAQ ID is required to delete an FAQ.");
  }

  if (!FAQ_API_ENDPOINT) {
    throw new Error(
      "FAQ API endpoint is not configured in environment variables."
    );
  }

  const response = await fetch(`${FAQ_API_ENDPOINT}/delete_faqs/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      input: {
        faq_id: faqId,
      },
    }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    console.error("Failed to delete FAQ:", response.status, errorData);
    throw new Error(`Failed to delete FAQ. Status: ${response.status}`);
  }

  const data = await response.json();
  if (!data.output?.status) {
    throw new Error(data.output?.message || "Failed to delete FAQ");
  }

  return data;
};

// update an existing faq
export const updateFaq = async (payload: UpdateFaqPayload): Promise<any> => {
  if (!payload.faq_id) {
    throw new Error("FAQ ID (faq_id) is required to update an FAQ.");
  }

  if (!FAQ_API_ENDPOINT) {
    throw new Error(
      "FAQ API endpoint is not configured in environment variables."
    );
  }

  const maiAskUser = JSON.parse(localStorage.getItem("maiAskUser") || "{}");

  // Create the payload to be sent, mapping programs correctly
  const finalPayload = {
    ...payload,
    email: maiAskUser.email,
    prism_id: maiAskUser.prismId,
    // Map programs to include name, code, and pok (defaulting to null)
    programs: payload.programs
      ?.filter((p) => p.code !== undefined) // Ensure code exists
      .map((p) => ({
        name: p.name, // Use name from payload
        code: p.code!, // Use code from payload
        pok: p.pok || null, // Use pok from payload, default to null
      })),
  };

  const response = await fetch(`${FAQ_API_ENDPOINT}/update_faqs/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ input: finalPayload }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    console.error("Failed to update FAQ:", response.status, errorData);
    throw new Error(`Failed to update FAQ. Status: ${response.status}`);
  }

  const data = await response.json();
  if (!data.output?.status) {
    throw new Error(data.output?.message || "Failed to update FAQ");
  }

  return data;
};

const AUTH_TOKEN = "Bearer " + localStorage.getItem("maiaskToken");
// fetch label options for policy
export const fetchPolicyLabels = async (): Promise<
  { label: string; value: string; label_id: string }[]
> => {
  const response = await fetch(`${FAQ_API_ENDPOINT}/policy/label/get/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      authorization: AUTH_TOKEN,
    },
    body: JSON.stringify({ input: {} }),
  });
  if (!response.ok) {
    const errorData = await response.text();
    console.error("Failed to fetch policy labels:", response.status, errorData);
    throw new Error(
      `Failed to fetch policy labels. Status: ${response.status}`
    );
  }
  const data = await response.json();
  const labels = data?.output?.labels || [];
  console.log(labels, "labels");
  // Update this mapping to use the real ID
  return labels.map((item: any) => ({
    label: item.label || item.name || item.value,
    label_id: item.label_id || item.label || item.name,
    value: item.value || item.label || item.name,
  }));
};

// Add a new policy
export const addPolicy = async ({
  program_code,
  label_id,
  description,
}: {
  program_code: string;
  label_id: string;
  description: string;
}): Promise<any> => {
  const FAQ_API_ENDPOINT = process.env.REACT_APP_BOT_END_POINT;
  const response = await fetch(`${FAQ_API_ENDPOINT}/policy/add/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      authorization: AUTH_TOKEN,
    },
    body: JSON.stringify({ input: { program_code, label_id, description } }),
  });
  const data = await response.json();
  if (data.output?.status == "failed") {
    throw new Error(data.output?.message || "Failed to update FAQ");
  }
  return data;
};

// Get policy by program code
export const getPolicyByProgramAndLabel = async ({
  program_code,
}: {
  program_code: string;
}): Promise<any> => {
  const response = await fetch(`${FAQ_API_ENDPOINT}/policy/get/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      authorization: AUTH_TOKEN,
    },
    body: JSON.stringify({ input: { program_code } }),
  });
  const data = await response.json();
  if (data.output?.status == "failed") {
    throw new Error(data.output?.message || "Failed to update FAQ");
  }
  return data;
};

// Add after getPolicyByProgramAndLabel
export const editPolicy = async ({
  program_code,
  label_id,
  description,
  policy_id,
}: {
  program_code: string;
  label_id: string;
  description: string;
  policy_id: string;
}): Promise<any> => {
  if (!FAQ_API_ENDPOINT) {
    throw new Error(
      "FAQ API endpoint is not configured in environment variables."
    );
  }
  const response = await fetch(`${FAQ_API_ENDPOINT}/policy/edit/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      authorization: AUTH_TOKEN,
    },
    body: JSON.stringify({
      input: {
        program_code,
        label_id,
        description,
        policy_id,
      },
    }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (data.output?.status == "failed") {
    throw new Error(data.output?.message || "Failed to update edit policy");
  }
  return data;
};

// Fetch a single policy by program_code and label_id
export const getPolicyByLabelId = async ({
  policy_id,
}: {
  policy_id: string;
}): Promise<any> => {
  console.log("Fetching policy by label_id:", policy_id);
  const payload: any = { policy_id };
  const response = await fetch(`${FAQ_API_ENDPOINT}/policy/get/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      authorization: AUTH_TOKEN,
    },
    body: JSON.stringify({ input: payload }),
  });
  if (!response.ok) {
    const errorData = await response.text();
    console.error(
      "Failed to fetch policy by label_id:",
      response.status,
      errorData
    );
    throw new Error(
      `Failed to fetch policy by label_id. Status: ${response.status}`
    );
  }
  const data = await response.json();

  // If the API returns a single object
  return { output: { policy: data?.output?.policy[0] } };
};

// Delete a policy by  policy_id
export const deletePolicy = async ({
  program_code,
  policy_id,
}: {
  program_code: string;
  policy_id: string;
}): Promise<any> => {
  const response = await fetch(`${FAQ_API_ENDPOINT}/policy/delete/invoke`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      authorization: AUTH_TOKEN,
    },
    body: JSON.stringify({ input: { policy_id } }),
  });
  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(
      `Failed to delete policy. Status: ${response.status}. ${errorData}`
    );
  }
  return response.json();
};
